// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngCarousel from "@tps/App/Components/XngCarousel";
import { XngCustomGroup } from "@tps/XDL/constants";
import React from "react";

const enum fields {
    testimonials = "testimonials",
}

const enum testimonialFields {
    copy = "copy",
    author = "author",
    company = "company",
}

interface TestimonialModel {
    fields: {
        copy: string;
        author: string;
        company: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.testimonials]: TestimonialModel[];
    };
}

type Props = OwnProps;

export interface TestimonialsStyles {
    container?: InjectableCss;
    testimonialContainer?: InjectableCss;
    copyText?: TypographyProps;
    authorText?: TypographyProps;
}

export const testimonialsStyles: TestimonialsStyles = {};

const styles = testimonialsStyles;

const Testimonials = ({ id, fields: { testimonials } }: Props) => {
    const renderTestimonials = testimonials.map((testimonial, index) => {
        const { copy, author, company } = testimonial.fields;
        return (
            <StyledWrapper
                key={`${author}_${index}`}
                {...styles.testimonialContainer}
                data-test-selector={`testimonial-of-company-${company}-by-${author}`}
            >
                <Typography {...styles.copyText}>{copy}</Typography>
                <Typography {...styles.authorText}>{`${author} | ${company}`}</Typography>
            </StyledWrapper>
        );
    });
    const haveMultipleItems = testimonials.length > 1;

    return (
        <StyledWrapper {...styles.container}>
            <XngCarousel
                spaceBetween={0}
                slidesPerView={1}
                scrollbar={false}
                navigation={haveMultipleItems}
                centerInsufficientSlides={false}
                pagination={haveMultipleItems && { clickable: true }}
                loop={true}
                autoplay={haveMultipleItems}
            >
                {renderTestimonials}
            </XngCarousel>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: Testimonials,
    definition: {
        group: XngCustomGroup,
        icon: "list-ul",
        isSystem: true,
        fieldDefinitions: [
            {
                name: fields.testimonials,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.copy.substring(0, 30);
                },
                defaultValue: [],
                fieldType: "Translatable",
                fieldDefinitions: [
                    {
                        name: testimonialFields.copy,
                        displayName: translate("Copy"),
                        editorTemplate: "MultilineTextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: testimonialFields.author,
                        displayName: translate("Author Name"),
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: testimonialFields.company,
                        displayName: translate("Company Name"),
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
